import React, {
    useCallback, useEffect, useMemo, useState, useContext
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { appendMetadataWithIndex } from 'gw-jutro-adapters-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { TranslatorContext } from '@jutro/locale';
import { useDependencies } from 'gw-portals-dependency-react';
import { ServiceManager } from '@jutro/services';
import { useValidation } from 'gw-portals-validation-react';
import AddressLookupInfoComponent from 'gw-capability-policychange-ho-react/components/AddressLookup/AddressLookupComponent';
import { ModalNextProvider } from '@jutro/components';
import metadata from './VehicleComponent.metadata.json5';
import messages from './VehicleComponent.messages';
import styles from './VehicleComponent.module.scss';
import AddressComponent from '../../../nexus-capability-gateway-react/Components/Address/AddressComponent';
import AdditionalInsuredInterestComponent from 'gw-capability-policychange-ho-react/components/AdditionalInsuredInterest/AdditionalInsuredInterestComponent';
import PortalUtil from '../../../../utils/PortalUtil';

function VehicleComponent(props) {
    const {
        data: vehicleVM,
        readOnlyFields,
        labelPosition,
        showOptional,
        path,
        id,
        index,
        onValidate,
        onValueChange,
        submissionVM,
        authHeader,
        updateWizardData,
        isPolicyChange,
        setIsVinEditMode,
        isReplace,
        locationValidation
    } = props;
    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const [addLocation, setAddLocation] = useState(false);
    const [editLocation, setEditLocation] = useState(false);
    const [locationVM, setLocationVM] = useState({});
    const viewModelService = useContext(ViewModelServiceContext);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { EndorsementService } = useDependencies('EndorsementService');
    //const [classOfUse, setClassOfUse] = useState('');
    const localeService = ServiceManager.getService('locale-service');
    const translator = useContext(TranslatorContext);
    const [isGaragedOutsideUSA_EY, setIsGaragedOutsideUSA_EY] = useState(false);
    const { dateUtil } = PortalUtil;

    const useOfVehicleList = [ 'artisan','contractors','florist','gardenersandhorticulturists','greenhouseoperators','horticulturists','landscapegardner','marketgardeners','nurserymen'];

    const formattedMetadata = useMemo(() => {
        return appendMetadataWithIndex(metadata.pageContent, index);
    }, [index]);

    const [accountContacts, setAccountContacts] = useState([]);

    const engineTypeCodeValues = () => {
        const excludeElectricVehicle = [
            'TwoStroke',
            'FourStroke'
        ];
        const excludeConvertedOnly = [
            'Electric_EY',
            'TwoStroke',
            'FourStroke'
        ];
        const filteredValues = vehicleVM.engineType_EY.aspects.availableValues
            .filter((typeCode) => {
                if (vehicleVM.isARFCoverableEffectiveApr2025.value) {
                    return excludeConvertedOnly.includes(typeCode.code);
                }
                return excludeElectricVehicle.includes(typeCode.code);
            })
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredValues;
    };

    const useOfVehicleFieldValues = () => {
        const excludeNewIRCAVehTypes = [
            'autoaccessoriesandparts',
            'cementblockbuildingblocksbrick',
            'drugstoreretail',
            'furnituremanufacturersdistributors',
            'softdrinkmanufacturersditstributors',
            'steelmanufacturersdistributors',
            'fruitdealers',
            'Icecreammanufacturersdistributors'
        ];
        const filteredValues = vehicleVM.useOfVehicle_EY.aspects.availableValues
            .filter((typeCode) => {
                if (vehicleVM.isARFCoverableEffectiveApr2025.value !== undefined && vehicleVM.isARFCoverableEffectiveApr2025.value === false) {
                    return !excludeNewIRCAVehTypes.includes(typeCode.code);
                }
                return true;
            })
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredValues;
    };

    useEffect(() => {
        const quoteID = _.get(submissionVM, 'quoteID.value');
        if (!_.get(vehicleVM, 'garagedAt.value')) {
            _.set(vehicleVM, 'garagedAt.value', submissionVM.baseData.policyLocations.value[0].publicID);
        }
        setGarageLocation(vehicleVM.garagedAt.value)
        if (isPolicyChange) {
            _.set(vehicleVM, 'additionalPageValidation.value', true);
        }
        // if (vehicleVM.classCode && vehicleVM.classCode.value) {
        //     setClassOfUse(vehicleVM.classCode.value);
        // }
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
        /*ARF 2023 - If auto rate filing is not available for the vehicle instance, we would set it to the baline flag isEffectiveFebAutoRateFiling_EY */
        if (_.isUndefined(vehicleVM.isVehicleEffectiveFebAutoRateFiling_EY.value)) {
            _.set(vehicleVM, 'isVehicleEffectiveFebAutoRateFiling_EY.value', submissionVM.lobData.businessAuto.coverables.isEffectiveFebAutoRateFiling_EY.value);
        }
        if (_.isUndefined(vehicleVM.isARFCoverableEffectiveJun2024.value)) {
            _.set(vehicleVM, 'isARFCoverableEffectiveJun2024.value', submissionVM.lobData.businessAuto.coverables.isEffectiveJunAutoRateFiling_EY.value);
        }
        if (_.isUndefined(vehicleVM.isARFCoverableEffectiveApr2025.value) && !isPolicyChange) {
            _.set(vehicleVM, 'isARFCoverableEffectiveApr2025.value', submissionVM.lobData.businessAuto.coverables.isAutoFilingRateApr25Applicable_EY.value);
        }
        if (vehicleVM.rateAsOfDate.value !== undefined) {
            const rateAsOfDate = dateUtil.toDate(_.get(vehicleVM, 'rateAsOfDate.value'));
            const arf2025 = new Date(2025, 3, 1);
            if (isPolicyChange && rateAsOfDate !== undefined && rateAsOfDate >= arf2025) {
                vehicleVM.isARFCoverableEffectiveApr2025.value = true;
            }
        }

        const _contacts = submissionVM.baseData.account.accountContacts.value.map((c) => {
            c.id = c.publicID;
            return c;
        });
        _contacts.push({ id: 'placeholder', displayName: 'Search Contact' });
        setAccountContacts(_contacts);
    }, [id, onValidate, isComponentValid, vehicleVM]);

    const getDistanceRadius = useCallback(() => {
        const classOfUse = vehicleVM.classOfUse.value;
        if (classOfUse === '37- Farm Plus' || classOfUse === '38- Farm Plus') {
            vehicleVM.distanceRadiusNonHorseFilter.value = 'onetohundred';
        } else if (classOfUse === '39- Farm Plus Extended' || classOfUse === '40- Farm Plus Extended') {
            vehicleVM.distanceRadiusNonHorseFilter.value = 'onetotwofifty';
        }
        _.set(submissionVM, `lobData.businessAuto.coverables.vehicles.children.${index}.value`, vehicleVM.value);
        updateWizardData(submissionVM);
    }, [index, submissionVM, updateWizardData, vehicleVM.classOfUse.value, vehicleVM.distanceRadiusNonHorseFilter.value, vehicleVM.value]);

    const getClassOfUse = useCallback(
        async (deliveryData, useOfVehicle, isARFEffectiveJun2024) => {
            const classOfUse = await EndorsementService.getClassOfUse(deliveryData, useOfVehicle, isARFEffectiveJun2024, authHeader);
            if (classOfUse.length === undefined) {
                _.set(vehicleVM, 'classOfUse.value', undefined);
            } else {
                _.set(vehicleVM, 'classOfUse.value', classOfUse);
            }
            _.set(submissionVM, `lobData.businessAuto.coverables.vehicles.children.${index}.value`, vehicleVM.value);
            getDistanceRadius();
        }, [EndorsementService, authHeader, getDistanceRadius, submissionVM, index, vehicleVM, updateWizardData]
    );

    const updateAccountContacts = (contacts) => {
        const _contacts = contacts.map((c) => {
            c['id'] = c.publicID;
            return c;
        });
        _contacts.push({ id: 'placeholder', displayName: 'Search Contact' });
        setAccountContacts(_contacts);
    };

    const updatePackage = (value, index) => {
        const newSubmissionVM = viewModelService.clone(submissionVM);
        _.set(newSubmissionVM, `lobData.businessAuto.coverables.vehicles.children.${index}.value`, value);
        updateWizardData(newSubmissionVM);
    }

    const writeValue = useCallback(
        (value, path) => {
            _.set(vehicleVM, path, value);
            updatePackage(vehicleVM.value);
        },
        [vehicleVM]
    );

    const hasCommOrPPV = () => {
        var CommorPPVExist = submissionVM.lobData.businessAuto.coverables.vehicles.value.filter((v)=>{
            return v.vehicleType == "Commercial" || v.vehicleType == "PrivatePassenger_EY"
        }).length ?   true :  false

        return CommorPPVExist;
    };

    const handleEngineDisplacementChange = (event, props) => {
        const { value } = props;
        const vehicleType = _.get(vehicleVM, 'vehicleType.value.code');
        if (vehicleType === 'Motorcycle_EY' && value <= 40) {
            onValueChange(true, `${path}.isMoped_EY`);
        }
    };

    const populateVICCCBasedOnPurchasePrice = useCallback(
        (vehicleVM) => {
            const trailerVehicles = ['Trailer_EY','Motorcycle_EY','ATV_EY','Snowvehicle_EY'];
            const personalVehType = ['ATV_EY','ClassicAntique_EY','Motorcycle_EY','Motorhome_EY','PrivatePassenger_EY','Snowvehicle_EY','Trailer_EY','antique_ey'];
            let purchase = vehicleVM.purchasePrice.amount && vehicleVM.purchasePrice.amount.value != undefined  ? _.get(vehicleVM, 'purchasePrice.amount.value') : 0;
            let vehicleType = _.get(vehicleVM, 'vehicleType.value.code');
            var roundedPurchaseValue= (Math.round(purchase / 1000)).toString();
            var viccCodeEnd="";
            var calucated_trim_EY = "";

            if(roundedPurchaseValue.length == 1){
                viccCodeEnd="00" + roundedPurchaseValue;
            }
            else if(roundedPurchaseValue.length == 2){
                viccCodeEnd="0" + roundedPurchaseValue;
            }
            else if(roundedPurchaseValue.length == 3){
                viccCodeEnd=roundedPurchaseValue;
            }

            if(personalVehType.includes(vehicleType)){
                if (roundedPurchaseValue.length == 4) {
                    viccCodeEnd = roundedPurchaseValue;
                } else if (roundedPurchaseValue.length > 4) {
                    viccCodeEnd = roundedPurchaseValue.substring(0, 4);
                }
                if (vehicleType == "Trailer_EY") {
                    calucated_trim_EY = "T" + viccCodeEnd;
                } else if (!trailerVehicles.includes(vehicleType)) {
                    calucated_trim_EY = "P" + viccCodeEnd;
                }
            }
            else{
                if(roundedPurchaseValue.length > 3){
                    viccCodeEnd = roundedPurchaseValue.substring(0,3);
                }
                if(vehicleType == "CommercialVehTrailer_EY"){
                    calucated_trim_EY = "T" + viccCodeEnd;
                } else {
                    calucated_trim_EY = "C" + viccCodeEnd;
                }
            }
            _.set(vehicleVM, 'viccCode.value', calucated_trim_EY);
            return calucated_trim_EY;
        },[vehicleVM]
    );

    const handleValueChange = useCallback(
        (value, changedPath) => {
            const newPath = changedPath.replace(/^vehicle\./, '');
            const fullPath = `${path}.${newPath}`;
            const primaryUseNewPath = 'primaryUse';
            const primaryUseFullPath = `${path}.${primaryUseNewPath}`;
            // if(newPath == 'useOfVehicle_EY' || newPath == 'delivery_EY' || newPath == 'typeOfUse_EY'){
            //     setClassOfUse('');
            // }

            if(newPath == 'garagedAt'){
                setGarageLocation(value)
            }

            if(newPath == 'purchasePrice' || newPath == 'listPrice'){
                if(vehicleVM?.omiaVin_EY.value == false || vehicleVM?.omiaVin_EY?.value == undefined){
                    var updatedVICC = populateVICCCBasedOnPurchasePrice(vehicleVM);
                    _.set(vehicleVM, 'viccCode.value', updatedVICC);
                }
                const newSubmissionVM = viewModelService.clone(submissionVM);
                _.set(newSubmissionVM, `lobData.businessAuto.coverables.vehicles.children.${index}.value`, vehicleVM.value);
                updateWizardData(newSubmissionVM);
            }

            // ARF 2023 - DE1719 - Out of Province� value defaulting to �null� when isOperatedOutsideProvince_EY is selected as false
            if(newPath == 'isOperatedOutsideProvince_EY' && value == false){
                _.set(vehicleVM, 'outOfProvince_EY.value', '');
                _.set(vehicleVM, 'outOfProvinceTime_EY.value', '');
            }

            if (onValueChange) {
                if (value === 'CommercialVehTrailer_EY') {
                    if (!hasCommOrPPV()) {
                        ModalNextProvider.showAlert({
                            title: 'Error',
                            message: 'Commercial Vehicle -Trailer needs a towing vehicle to be added. Please add a supporting vehicle to proceed.',
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: 'Ok'
                        }).catch(_.noop);
                    } else {
                        onValueChange(value, fullPath);
                        onValueChange('business', primaryUseFullPath);
                    }
                }
                else if (value === 'Commercial' || value === 'FarmBuses_EY' || value === 'FuneralDirectors') {
                    onValueChange(value, fullPath);
                    onValueChange('business', primaryUseFullPath);
                }
                else if (value === 'Snowvehicle_EY' || value === 'ATV_EY' || value === 'Trailer_EY' || value === 'Motorhome_EY') {
                    onValueChange(value, fullPath);
                    onValueChange('pleasure', primaryUseFullPath);
                }
                else {
                    onValueChange(value, fullPath);
                }
            }
        },
        [onValueChange, path]
    );

    const primaryUseValueChange = useCallback((value, changedPath) => {
        const newPath = changedPath.replace(/^vehicle\./, '');
        _.set(vehicleVM.value, newPath, value);
        const primaryUseOfVeh = vehicleVM.primaryUse.value.code
        if (primaryUseOfVeh && primaryUseOfVeh === 'business') {
            _.set(vehicleVM, 'businessPercentage.value', 100);
        } else {
            _.set(vehicleVM, 'businessPercentage.value', 0);
            _.set(vehicleVM, 'commutingMiles.value', null);
            _.set(vehicleVM, 'personalPercentage_EY.value', null);
        }
        if (primaryUseOfVeh && primaryUseOfVeh === 'commuting') {
            _.set(vehicleVM, 'commuteDaysPerWeek_EY.value', 5);
        } else {
            _.set(vehicleVM, 'commuteDaysPerWeek_EY.value', null);
        }
        updateWizardData(submissionVM);
    }, [vehicleVM, updateWizardData, submissionVM]);

    const generateFieldsVisibilityOverride = () => {
        const fieldsList = [
            {
                fields: ['isTowingTrailer_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: 'Commercial'
            },
            {
                fields: ['funeralDirectorVehType_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: 'FuneralDirectors'
            },
            {
                fields: ['numOfSeats_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: 'FarmBuses_EY'
            },
            {
                fields: ['isTrailerSupplCarryCapcity_EY', 'trailerType_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: 'CommercialVehTrailer_EY'
            },
            {
                fields: ['trailerDescribe_EY'],
                path: 'trailerType_EY.value.code',
                valueToMatch: 'other'
            },
            {
                fields: ['bodyStyle_EY', 'isSlowMovingVehicle_EY', 'atvWheels_EY', 'isOffRoadMotorCycle_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: 'ATV_EY'
            },
            {
                fields: ['bodyType'],
                path: 'vehicleType.value.code',
                valueToMatch: 'Trailer_EY'
            },
            // {
            //     fields: ['engineType_EY'],
            //     /*ARF 2023 - Sets visibility of the field based on either one of the vehicle types AND
            //             if ATV we check for the  flag isVehicleEffectiveFebAutoRateFiling_EY and OFFroadvehicle*/
            //     conditional:[
            //         [{
            //             path: 'vehicleType.value.code',
            //             valueToMatch: ['ATV_EY'],
            //         },
            //         {
            //             path: 'isVehicleEffectiveFebAutoRateFiling_EY.value',
            //             valueToMatch: true
            //         },
            //         {
            //             path: 'isOffRoadMotorCycle_EY.value',
            //             valueToMatch: true,
            //         },],
            //         [{
            //             path: 'vehicleType.value.code',
            //             valueToMatch: ['Snowvehicle_EY']

            //         }]
            //     ]
            // },
            {
                fields: ['outOfProvince_EY', 'outOfProvinceTime_EY'],
                path: 'isOperatedOutsideProvince_EY.value',
                valueToMatch: true
            },
            // {
            //     fields: ['pistonDisplacement_EY'],
            //     path: 'vehicleType.value.code',
            //     valueToMatch: ['Snowvehicle_EY', 'ATV_EY', 'Motorcycle_EY']
            // },
            {
                fields: ['underAgeDrivers_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: ['Snowvehicle_EY', 'ATV_EY']
            },
            {
                fields: ['vehicleAppraisalLabel', 'appraisalDate_EY', 'appraisalValue_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: ['antique_ey', 'ClassicAntique_EY']
            },
            {
                fields: ['businessPercentage'],
                path: 'primaryUse.value.code',
                valueToMatch: ['business']
            },
            // {
            //     fields: ['personalPercentage'],
            //     /*ARF 2023 - Sets visibility of the field based on the primaryUse AND
            //             if ATV we check for the  flag isVehicleEffectiveFebAutoRateFiling_EY and OFFroadvehicle*/
            //     conditional: [
            //         [{
            //             path: 'primaryUse.value.code',
            //             valueToMatch: 'business'
            //         }],
            //         [{
            //             path: 'vehicleType.value.code',
            //             valueToMatch: ['FarmBuses_EY'],
            //         }]
            //     ]
            // },
            // {
            //     fields: ['annualMileage'],
            //     path: 'vehicleType.value.code',
            //     valueNotToMatch: ['CommercialVehTrailer_EY', 'Trailer_EY']
            // },
            // {
            //     fields: ['commutingMiles'],
            //     path: 'primaryUse.value.code',
            //     valueNotToMatch: ['commuting']
            // },
            {
                fields: ['afterMarketTheftDevice'],
                path: 'vehicleType.value.code',
                valueNotToMatch: ['Snowvehicle_EY']
            },
            {
                fields: ['trailmasterDiscount_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: ['Snowvehicle_EY']
            },
            {
                fields: ['genNowDiscount_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: ['PrivatePassenger_EY']
            },
            {
                fields: ['usExposurePercent'],
                path: 'usExposure.value',
                valueToMatch: true
            },
            {
                fields: ['customizedAmount', 'customisationType_EY', 'customizationDescribe_EY'],
                path: 'vehicleCustomized.value',
                valueToMatch: true
            },
            {
                fields: ['delivery_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: ['CommercialVehTrailer_EY', 'Commercial']
            },
            {
                fields: ['distanceRadiusFarmBuses'],
                path: 'vehicleType.value.code',
                valueToMatch: ['FarmBuses_EY']
            },
            {
                fields: ['destinationCities_EY', 'seasonalDelivery_EY', 'haulNonOwnedTrailers_EY', 'cvorNumber_EY', 'covrRating_EY', 'covrViolationRate_EY', 'covrReportDate_EY', 'covrReOrderDate_EY', 'grossWeight_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: ['CommercialVehTrailer_EY', 'FarmBuses_EY', 'FuneralDirectors', 'Commercial']
            },
            {
                fields: ['bodyWeightCode'],
                path: 'vehicleType.value.code',
                valueToMatch: [ 'FarmBuses_EY', 'FuneralDirectors', 'Commercial']
            },
            {
                fields: ['radiusNumOfKms_EY', 'radiusNumOfTrips_EY'],
                path: 'seasonalDelivery_EY.value',
                valueToMatch: true
            },
            {
                fields: ['describe_EY'],
                path: 'radiusNumOfTrips_EY.value',
                greaterThan: 0
            },
            {
                fields: ['description_EY'],
                path: 'haulNonOwnedTrailers_EY.value',
                valueToMatch: true
            },
            /* {
                fields: ['towingVehicle_EY'],
                path: 'vehicleType.value.code',
                valueToMatch: 'CommercialVehTrailer_EY'
            },*/
            {
                fields: ['multiVehicleDiscount_EY', 'engineCylinders'],
                path: 'vehicleType.value.code',
                valueToMatch: 'PrivatePassenger_EY'
            },
            {
                fields: ['winterTireInstDate'],
                path: 'winterTires.value',
                valueToMatch: true
            },
            {
                fields: ['isTouring', 'isMoped'],
                path: 'vehicleType.value.code',
                valueToMatch: 'Motorcycle_EY'
            },
            {
                fields: ['numberOfLocations'],
                /*ARF 2023 - Sets visibility of the fields based on either one of the vehicle types AND the  flag isVehicleEffectiveFebAutoRateFiling_EY*/

                conditional: [
                    [{
                        path: 'vehicleType.value.code',
                        valueToMatch: ['FarmBuses_EY'],
                    },
                    {
                        path: 'isVehicleEffectiveFebAutoRateFiling_EY.value',
                        valueToMatch: true,
                    }],
                    [{
                        path: 'vehicleType.value.code',
                        valueToMatch: ['FuneralDirectors'],
                    },
                    {
                        path: 'isVehicleEffectiveFebAutoRateFiling_EY.value',
                        valueToMatch: true,
                    }],
                    [{
                        path: 'vehicleType.value.code',
                        valueToMatch: ['Commercial'],
                    },
                    {
                        path: 'isVehicleEffectiveFebAutoRateFiling_EY.value',
                        valueToMatch: true,
                    }]
                ]
            },
        ];

        const overrides = fieldsList.map((fs) => {
            const ovrRides = fs.fields.map((field) => {
                let isVisible = false;
                if (Array.isArray(fs.valueToMatch)) {
                    _.each(fs.valueToMatch, (valueToMatch) => {
                        if (!isVisible) {
                            isVisible = _.get(vehicleVM, `${fs.path}`) === valueToMatch;
                        }
                    });
                } else if (fs.greaterThan !== undefined) {
                    isVisible = _.get(vehicleVM, `${fs.path}`) > fs.greaterThan;
                } else if (fs.lesserThan !== undefined) {
                    isVisible = _.get(vehicleVM, `${fs.path}`) < fs.greaterThan;
                } else {
                    isVisible = _.get(vehicleVM, `${fs.path}`) === fs.valueToMatch;
                }
                if (Array.isArray(fs.valueNotToMatch)) {
                    _.each(fs.valueNotToMatch, (valueNotToMatch) => {
                        if (!isVisible) {
                            isVisible = _.get(vehicleVM, `${fs.path}`) !== valueNotToMatch;
                        }
                    });
                }
                if (fs.conditional) {
                    let isOneConditionPassed = false;
                    _.each(fs.conditional, (condition) => {
                        let isAllInternalConditionPassed = true;
                        _.each(condition, (c) => {
                            if(Array.isArray(c.valueToMatch)){
                                var value =  _.get(vehicleVM, `${c.path}`);
                                if(c.valueToMatch.indexOf(value) == -1){
                                    isAllInternalConditionPassed = false
                                }
                            }
                            else if(_.get(vehicleVM, c.path) != c.valueToMatch){
                                isAllInternalConditionPassed = false
                            }
                        });
                        if (!isOneConditionPassed) {
                            isOneConditionPassed = isAllInternalConditionPassed;
                        }
                    });
                    isVisible = isOneConditionPassed;
                }
                if (!isVisible) _.set(vehicleVM, `vehicle.${field}`, undefined);
                const readOnlyfields = ['engineCylinders'];
                if (readOnlyfields.indexOf(field) !== -1) {
                    return {
                        [`${field}${index}`]: {
                            visible: isVisible,
                            readOnly: isPolicyChange
                        }
                    };
                }
                return {
                    [`${field}${index}`]: {
                        visible: isVisible
                    }
                };
            });

            return Object.assign({}, ...ovrRides);
        });

        const _overrides = Object.assign({}, ...overrides);
        if (_overrides[`pistonDisplacement_EY${index}`]) {
            _overrides[`pistonDisplacement_EY${index}`].onBlur = handleEngineDisplacementChange;
        }
        return _overrides;
    };

    const createLocationVM = (model) => {
        const _locationVM = viewModelService.create(
            model,
            'pc',
            'edge.capabilities.policyjob.helper.PolicyLocationDTO'
        );
        return _locationVM;
    };

    const handleAddNewLocation = () => {
        const model = { country: localeService.getDefaultCountryCode(), state: 'ON', isValidationRequired: true };
        const locationNewVM = createLocationVM(model);
        setLocationVM(locationNewVM);
        setAddLocation(true);
        if (locationNewVM.aspects.invalidChildProperties.length > 0) {
            locationValidation(true);
        }
        setEditLocation(false);
    };

    const handleEditNewLocation = () => {
        const selectedLocation = _.get(vehicleVM, 'garagedAt.value');
        const model = submissionVM.baseData.policyLocations.value
            .filter((l) => { return l.publicID === selectedLocation; })[0];
        model.isValidationRequired = true;
        const locationNewVM = createLocationVM(model);
        setLocationVM(locationNewVM);
        if (locationNewVM.aspects.invalidChildProperties.length > 0) {
            locationValidation(true);
        }
        setAddLocation(false);
        setEditLocation(true);
    };

    const cancelLocation = async () => {
        setAddLocation(false);
        setEditLocation(false);
        locationValidation(false);
    };

    // ARF - 2023 - Vehicles garaged in the U.S. for 6 months or more in a policy term
    const setGarageLocation = (value) => {
        const garagedAt = submissionVM.baseData.policyLocations.value.filter((l) => { return l.publicID === value})[0];
        if(garagedAt && garagedAt.country == "US") {
            setIsGaragedOutsideUSA_EY(true)
        } else{
            setIsGaragedOutsideUSA_EY(false)
        }
    }


    const createLocation = async () => {
        let updatedLocation = {};

        if (isPolicyChange) {
            const jobID = _.get(submissionVM, 'jobID.value');
            updatedLocation = await EndorsementService.createOrUpdatePolicyLocation(
                [jobID, locationVM.value], authHeader
            );
            if (addLocation) {
                _.set(vehicleVM, 'garagedAt.value', updatedLocation.publicID);
            }
            setAddLocation(false);
            setEditLocation(false);
            locationValidation(false);
            submissionVM.value = await EndorsementService.saveEndorsement(
                [submissionVM.value],
                authHeader
            );
        } else {
            const quoteID = _.get(submissionVM, 'quoteID.value');
            updatedLocation = await LoadSaveService.createOrUpdatePolicyLocation(
                [quoteID, locationVM.value, null], authHeader
            );
            if (addLocation) {
                _.set(vehicleVM, 'garagedAt.value', updatedLocation.publicID);
            }
            vehicleVM.isValidationRequired = false;
            setAddLocation(false);
            setEditLocation(false);
            locationValidation(false);
            submissionVM.value = await LoadSaveService.updateDraftSubmission(
                submissionVM.value, authHeader
            );
            vehicleVM.isValidationRequired = true;
        }

        const newSubmissionVM = viewModelService.clone(submissionVM);
        updateWizardData(newSubmissionVM);
        setAddLocation(false);
        setEditLocation(false);
        locationValidation(false);
    };

    const OnAddressValueChange = (value, path) => {
        const newLocationVM = viewModelService.clone(locationVM);
        _.set(newLocationVM, path, value);
        setLocationVM(newLocationVM);
    };

    const classOfUseUpdated = useCallback((value, changedPath) => {
        const newPath = changedPath.replace(/^vehicle\./, '');
        _.set(vehicleVM.value, newPath, value);
        const deliveryData = _.get(vehicleVM.value, 'delivery_EY');
        const useOfVehicle =  _.get(vehicleVM.value, 'useOfVehicle_EY');
        const isARFEffectiveJun2024 = vehicleVM.isARFCoverableEffectiveJun2024.value
        if(deliveryData || useOfVehicle){
            getClassOfUse(deliveryData, useOfVehicle, isARFEffectiveJun2024);
        }
    },
    [getClassOfUse, vehicleVM.value]
    );

    const getHaulLivestock = useCallback((value, changedPath) => {
        const newPath = changedPath.replace(/^vehicle\./, '');
        _.set(vehicleVM.value, newPath, value);
        const useOfVehicle = vehicleVM.useOfVehicle_EY.value.code
        if(useOfVehicle && (useOfVehicle == 'livestockradius160kms' || useOfVehicle == 'livestock161_400kmradius')){
            _.set(vehicleVM, 'haulLivestock_EY.value', true);
        } else {
            _.set(vehicleVM, 'haulLivestock_EY.value', false);
        }
        updateWizardData(submissionVM)
    }, [vehicleVM, updateWizardData, submissionVM] );

    const vehicleTypeUpdated = useCallback((value, changedPath) => {
        const newPath = changedPath.replace(/^vehicle\./, '');
        _.set(vehicleVM.value, newPath, value);
        const personalVehType = ['PrivatePassenger_EY','CommercialVehTrailer_EY','Commercial','FarmBuses_EY','FuneralDirectors'];
        const vehicleType = vehicleVM.vehicleType.value.code
        if(!personalVehType.includes(vehicleType)){
            _.set(vehicleVM, 'rateBy.value', true);
        }
        updateWizardData(submissionVM)

    }, [vehicleVM, updateWizardData, submissionVM] );

    const useOfVehValueChange = useCallback((value, changedPath) => {
        classOfUseUpdated(value, changedPath)
        getHaulLivestock(value, changedPath)
    }, [classOfUseUpdated, getHaulLivestock]);

    const engineDisplacementVisibility = () => {
        const vehicleType = _.get(vehicleVM, 'vehicleType.value.code');
        const engineType = _.get(vehicleVM, 'engineType_EY.value.code');
        if (vehicleVM.isARFCoverableEffectiveApr2025.value && (((vehicleVM.isElectricVehicle_EY.value !== undefined && vehicleVM.isElectricVehicle_EY.value)
                && (vehicleType === 'ATV_EY' || vehicleType === 'Motorcycle_EY'))
            || (vehicleType === 'Snowvehicle_EY' && engineType === 'Electric_EY'))) {
            return true;
        }
        return false;
    };

    const pistonDisplacementVisibility = () => {
        const vehicleType = _.get(vehicleVM, 'vehicleType.value.code');
        const engineType = _.get(vehicleVM, 'engineType_EY.value.code');
        if (vehicleVM.isARFCoverableEffectiveApr2025.value) {
            return (((vehicleVM.isElectricVehicle_EY.value === undefined || vehicleVM.isElectricVehicle_EY.value === false)
            && (vehicleType === 'ATV_EY' || vehicleType === 'Motorcycle_EY'))
        || (vehicleType === 'Snowvehicle_EY' && engineType !== 'Electric_EY'));
        }
        return (vehicleType === 'ATV_EY' || vehicleType === 'Motorcycle_EY' || vehicleType === 'Snowvehicle_EY')
    };


    const classOfUse = vehicleVM.classOfUse.value;
    const personalVehType = ['PrivatePassenger_EY', 'CommercialVehTrailer_EY', 'Commercial', 'FarmBuses_EY', 'FuneralDirectors'];
    const vehicleType = vehicleVM.vehicleType.value.code;
    const trailerType = vehicleVM.trailerType_EY.value !== undefined ? vehicleVM.trailerType_EY.value.code : undefined;
    const overrideProps = {
        '@field': {
            labelPosition,

            showOptional: false,
            showRequired: true
        },
        [`vinLookup${index}`]: {
            readOnly: _.includes(readOnlyFields, 'vinlookup'),
            quoteCreationDate: _.get(submissionVM, 'baseData.quoteCreationDate.value'),
            isPolicyChange: isPolicyChange,
            setIsVinEditMode: setIsVinEditMode,
            isReplace: isReplace,
            policyEffectiveDate: _.get(submissionVM, 'baseData.periodStartDate.value'),
        },
        [`vehicleType${index}`]: {
            readOnly: isPolicyChange && vehicleVM.existingVehicle_EY.value,
            onValueChange: handleValueChange
        },
        [`vehicleViccCode${index}`]: {
            readOnly: isPolicyChange && vehicleVM.existingVehicle_EY.value
        },
        [`vehicleLicencePlate${index}`]: { readOnly: _.includes(readOnlyFields, 'vehicleLicencePlate') },
        [`driverLicenceState${index}`]: { readOnly: _.includes(readOnlyFields, 'driverLicenceState') },
        [`vehicleCostNew${index}`]: { readOnly: _.includes(readOnlyFields, 'vehicleCostNew') },
        [`garagedAt${index}`]: {
            availableValues: submissionVM.baseData.policyLocations.value.map((l) => { l.id = l.publicID; return l; })
        },
        [`editNewLocationButton${index}`]: {
            onClick: handleEditNewLocation,
            disabled: addLocation || editLocation
        },
        [`addNewLocationButton${index}`]: {
            onClick: handleAddNewLocation,
            disabled: addLocation || editLocation
        },
        [`addressLookupComponent${index}`]: {
            visible: addLocation || editLocation,
            model: locationVM,
            hideFields: ['addressLine3', 'addressType'],
            //readOnlyFields: ['country'], ARF 2023 - DE4919 - Allowing users to modify the country
            onValueChange: OnAddressValueChange
        },
        [`addressLookupButtonContainer${index}`]: {
            visible: addLocation || editLocation,
        },
        [`addAddressButton${index}`]: {
            onClick: createLocation,
            visible: addLocation,
            disabled: !_.isUndefined(locationVM.aspects) && locationVM.aspects.invalidChildProperties.length > 0
        },
        [`saveAddressButton${index}`]: {
            onClick: createLocation,
            visible: editLocation,
            disabled: !_.isUndefined(locationVM.aspects) && locationVM.aspects.invalidChildProperties.length > 0
        },
        [`cancelAddressButton${index}`]: {
            onClick: cancelLocation
        },
        [`vehicleFuelType${index}`]: { readOnly: vehicleVM.omiaVin_EY.value },
        [`purchasePrice${index}`]: {
            // visible: vehicleVM.neworUsed.value === undefined || vehicleVM.neworUsed.value.code === 'New' || vehicleVM.neworUsed.value.code === 'Demo',
            onValueChange: handleValueChange
        },
        [`listPrice${index}`]: {
            visible: vehicleVM.vehicleType.value.code === 'CommercialVehTrailer_EY' || vehicleVM.vehicleType.value.code === 'FarmBuses_EY' || vehicleVM.vehicleType.value.code === 'FuneralDirectors' || vehicleVM.vehicleType.value.code === 'Commercial',
            onValueChange: handleValueChange
        },
        // [`purchaseValue${index}`]: { visible: vehicleVM.neworUsed.value !== undefined && vehicleVM.neworUsed.value.code === 'Used' },
        [`additionalInterest${index}`]: {
            coverableVM: vehicleVM,
            accountContacts: accountContacts,
            accountNumber: _.get(submissionVM, 'baseData.accountNumber.value'),
            updatePackage: updatePackage,
            authHeader: authHeader,
            type: 'additionalInterest',
            onValueChange: writeValue,
            jobID: isPolicyChange ? _.get(submissionVM, 'jobID.value') : _.get(submissionVM, 'quoteID.value'),
            isPolicyChange: isPolicyChange ? isPolicyChange : false,
            updateAccountContacts: updateAccountContacts
        },
        [`towingVehicle_EY${index}`]: {
            availableValues: submissionVM.lobData.businessAuto.coverables.vehicles.value
                .filter((vehicle) => {
                    return (vehicle.vehicleType === 'Commercial' || vehicle.vehicleType === 'PrivatePassenger_EY') && vehicle.vehicleNumber !== vehicleVM.vehicleNumber.value;
                })
                .map((c) => {
                    if(c.publicID){
                        c['id'] = c.publicID;
                    } else {
                        c['id'] = c.vehicleNumber;
                        c['displayName'] = c.year +' '+c.make +' '+c.model;
                    }
                    return c
                }),
            visible: vehicleVM.vehicleType.value.code === 'CommercialVehTrailer_EY'
        },
        [`towingVehicleClassofUse_EY${index}`]: {
            visible: vehicleVM.towingVehicle_EY.value !== undefined
        },
        [`commutingMiles${index}`]: {
            visible: vehicleVM.primaryUse !== undefined && vehicleVM.primaryUse?.value?.code === 'commuting'
        },
        [`commuteDaysPerWeek_EY${index}`]: {
            visible: vehicleVM.primaryUse !== undefined && vehicleVM.primaryUse?.value?.code !== 'pleasure'
        },
        [`classOfUse${index}`]: {
            visible: vehicleVM.vehicleType.value.code === 'Commercial' || vehicleVM.vehicleType.value.code === 'CommercialVehTrailer_EY',
            value: vehicleVM.classOfUse ? vehicleVM.classOfUse.value : ''
        },
        [`typeOfUse_EY${index}`]: {
            visible: (vehicleVM.delivery_EY.value ? vehicleVM.delivery_EY.value.code === 'nodeliverylight' : false) && (vehicleVM.useOfVehicle_EY.value ? useOfVehicleList.includes(vehicleVM.useOfVehicle_EY.value.code) : false)
        },
        [`outOfProvince_EY${index}`]: {
            visible: ((vehicleVM.vehicleType.value.code === 'FarmBuses_EY' || vehicleVM.vehicleType.value.code === 'FuneralDirectors' || vehicleVM.vehicleType.value.code === 'Commercial')
                        && (!!vehicleVM.isVehicleEffectiveFebAutoRateFiling_EY.value) && (!!vehicleVM.isOperatedOutsideProvince_EY.value))
        },
        [`outOfProvinceTime_EY${index}`]: {
            visible: ((vehicleVM.vehicleType.value.code === 'FarmBuses_EY' || vehicleVM.vehicleType.value.code === 'FuneralDirectors' || vehicleVM.vehicleType.value.code === 'Commercial')
                       && (!!vehicleVM.isVehicleEffectiveFebAutoRateFiling_EY.value) && (!!vehicleVM.isOperatedOutsideProvince_EY.value))
        },
        [`garagedOutsideUSA_EY${index}`]: {
            visible: vehicleVM.isVehicleEffectiveFebAutoRateFiling_EY.value && (vehicleVM.vehicleType.value.code !== 'FarmBuses_EY' && vehicleVM.vehicleType.value.code !== 'Commercial') && isGaragedOutsideUSA_EY
        },
        [`distanceRadiusReadOnly${index}`]: {
            visible: !!vehicleVM.isARFCoverableEffectiveJun2024.value && vehicleVM.vehicleType.value.code !== 'FarmBuses_EY'
        && (classOfUse === '37- Farm Plus' || classOfUse === '38- Farm Plus' || classOfUse === '39- Farm Plus Extended' || classOfUse === '40- Farm Plus Extended')
        },
        [`distanceRadius${index}`]: {
            visible: (vehicleType === 'FuneralDirectors' || vehicleType === 'Commercial' || vehicleType === 'CommercialVehTrailer_EY')
        && vehicleVM.useOfVehicle_EY.value === undefined
        && !(classOfUse === '37- Farm Plus' || classOfUse === '38- Farm Plus' || classOfUse === '39- Farm Plus Extended' || classOfUse === '40- Farm Plus Extended')
        },
        [`distanceRadiusNonCommVeh${index}`]: {
            visible: vehicleVM.vehicleType.value.code !== 'FarmBuses_EY'
        && (vehicleVM.useOfVehicle_EY.value ? (vehicleVM.useOfVehicle_EY.value.code !== 'farmplus' && vehicleVM.useOfVehicle_EY.value.code !== 'farmplusextended' && vehicleVM.useOfVehicle_EY.value.code !== 'farmtrucks') : false)
         && trailerType !== 'horsetrailerslt2' && trailerType !== 'horsetrailers3_6'
        },
        [`distanceRadiusCommVeh${index}`]: {
            visible: vehicleVM.vehicleType.value.code !== 'FarmBuses_EY'
        && (vehicleVM.useOfVehicle_EY.value ? (vehicleVM.useOfVehicle_EY.value.code === 'farmplus' || vehicleVM.useOfVehicle_EY.value.code === 'farmplusextended' || vehicleVM.useOfVehicle_EY.value.code === 'farmtrucks') : false)
        && (classOfUse ? (classOfUse !== '37- Farm Plus' && classOfUse !== '38- Farm Plus' && classOfUse !== '39- Farm Plus Extended' && classOfUse !== '40- Farm Plus Extended') : false)
        && trailerType !== 'horsetrailerslt2' && trailerType !== 'horsetrailers3_6'
        },
        [`distanceRadiusHorseFilter${index}`]: {
            visible: (trailerType ? (trailerType === 'horsetrailerslt2' || trailerType === 'horsetrailers3_6') : false)
                && (classOfUse ? (classOfUse !== '37- Farm Plus' && classOfUse !== '38- Farm Plus' && classOfUse !== '39- Farm Plus Extended' && classOfUse !== '40- Farm Plus Extended') : false)
        },
        [`isTrailorUsedForPersonalUse_EY${index}`]: {
            visible: vehicleType === 'CommercialVehTrailer_EY' && vehicleVM.isARFCoverableEffectiveJun2024.value && trailerType === 'horsetrailerslt2'
        },
        [`vehicleRateBy${index}`]: {
            readOnly: !(personalVehType.includes(vehicleType))
        },
        [`haulLivestock_EY${index}`]: {
            readOnly: vehicleVM.useOfVehicle_EY.value ? (vehicleVM.useOfVehicle_EY.value.code === 'livestockradius160kms' || vehicleVM.useOfVehicle_EY.value.code === 'livestock161_400kmradius') : false,
            visible: vehicleType === 'CommercialVehTrailer_EY' || vehicleType === 'Commercial'
        },
        [`telematicsDiscount${index}`]: {
            visible: submissionVM.lobData.businessAuto.coverables.isEffectiveOct24AutoRateFiling_EY.value
                && vehicleVM.vehicleType.value.code === 'PrivatePassenger_EY'
        },
        [`enrolledInTelematicsInd${index}`]: {
            visible: submissionVM.lobData.businessAuto.coverables.isEffectiveOct24AutoRateFiling_EY.value
                && vehicleVM.vehicleType.value.code === 'PrivatePassenger_EY',
            required: submissionVM.lobData.businessAuto.coverables.value.drivers.filter((driver) => driver.isEnrolledInTelematics_EY === true).length > 0,
            showRequired: submissionVM.lobData.businessAuto.coverables.value.drivers.filter((driver) => driver.isEnrolledInTelematics_EY === true).length > 0
        },
        [`isElectricVehicle_EY${index}`]: {
            visible: vehicleVM.isARFCoverableEffectiveApr2025.value && (vehicleVM.vehicleType.value.code === 'ATV_EY' || vehicleVM.vehicleType.value.code === 'Motorcycle_EY')
        },
        [`pistonDisplacement_EY${index}`]: {
            visible: pistonDisplacementVisibility()
        },
        [`engineDisplacement_EY${index}`]: {
            visible: engineDisplacementVisibility()
        },
        [`engineType_EY${index}`]: {
            availableValues: engineTypeCodeValues(),
            visible: vehicleVM.vehicleType.value.code === 'Snowvehicle_EY' || (vehicleVM.vehicleType.value.code === 'ATV_EY' && (vehicleVM.isOffRoadMotorCycle_EY.value !== undefined && vehicleVM.isOffRoadMotorCycle_EY.value))
        },
        [`businessPercentage${index}`]: {
            visible: vehicleVM.primaryUse?.value?.code === 'business' && vehicleVM.vehicleType.value && vehicleVM.vehicleType.value.code !== 'CommercialVehTrailer_EY' && vehicleVM.vehicleType.value.code !== 'Trailer_EY'

        },
        [`personalPercentage${index}`]: {
            visible: (vehicleVM.primaryUse?.value?.code === 'business' && (vehicleVM.vehicleType.value.code === 'CommercialVehTrailer_EY'
                || vehicleVM.vehicleType.value.code === 'FarmBuses_EY'
                || vehicleVM.vehicleType.value.code === 'FuneralDirectors'
                || vehicleVM.vehicleType.value.code === 'Commercial')) || vehicleVM.vehicleType.value.code === 'FarmBuses_EY'
        },
        [`numberOfTripsPerYear${index}`]: {
            visible: (vehicleVM.vehicleType.value.code === 'CommercialVehTrailer_EY'
                || vehicleVM.vehicleType.value.code === 'FarmBuses_EY'
                || vehicleVM.vehicleType.value.code === 'FuneralDirectors'
                || vehicleVM.vehicleType.value.code === 'Commercial')
        // label: (vehicleVM.isARFCoverableEffectiveApr2025.value !== undefined && vehicleVM.isARFCoverableEffectiveApr2025.value) ? translator({
        //     id: 'quoteandbind.pa.directives.templates.pa-edit-vehicle.Number of Trips beyond Normal Radius Per Year',
        //     defaultMessage: 'Number of Trips beyond Normal Radius Per Year'
        // }) : translator({
        //         id: 'quoteandbind.pa.directives.templates.pa-edit-vehicle.Number of Trips/Year',
        //         defaultMessage: 'Number of Trips/Year'
        // })
        },
        //    [`kmsOverNormalRadius${index}`]: {
        //     visible: vehicleVM.isARFCoverableEffectiveApr2025.value !== undefined && vehicleVM.isARFCoverableEffectiveApr2025.value
        //    },
        [`useOfVehicle_EY${index}`]: {
            availableValues: useOfVehicleFieldValues(),
            visible: vehicleVM.vehicleType.value.code === 'CommercialVehTrailer_EY'
                || vehicleVM.vehicleType.value.code === 'Commercial'
        },
        ...generateFieldsVisibilityOverride()
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: setComponentValidation,
            classOfUseUpdated,
            getDistanceRadius,
            vehicleTypeUpdated,
            useOfVehValueChange,
            primaryUseValueChange
        },
        resolveComponentMap: {
            /* addressLookupInfoComponent: AddressLookupInfoComponent,*/
            addressComponent: AddressComponent,
            additionalInsuredInterestComponent: AdditionalInsuredInterestComponent
        }

    };

    const data = {
        vehicle: vehicleVM
    };
    return (
        <ViewModelForm
            model={data}
            uiProps={formattedMetadata}
            overrideProps={overrideProps}
            onValidationChange={setComponentValidation}
            onValueChange={handleValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={styles}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

VehicleComponent.propTypes = {
    data: PropTypes.shape({}),
    labelPosition: PropTypes.string,
    path: PropTypes.string,
    onValueChange: PropTypes.func.isRequired,
    onValidate: PropTypes.func.isRequired,
    readOnlyFields: PropTypes.arrayOf(PropTypes.string),
    showOptional: PropTypes.bool,
    index: PropTypes.string,
    id: PropTypes.string.isRequired
};
VehicleComponent.defaultProps = {
    data: {},
    labelPosition: 'left',
    path: undefined,
    readOnlyFields: [],
    showOptional: true,
    index: 0
};
export default VehicleComponent;
